import {appUrl} from '@frontend/AppUrl'
import {MetaTags} from '@frontend/components/MetaTags'
import {trans} from '@frontend/helpers'
import {useConfigureCabinetUi} from '@frontend/hooks'
import {
    useAppRootStore,
    useAuthStore,
    useModalsStore,
} from '@frontend/stores'
import type {UserData} from '@frontend/types/User'
import {
    type ReactNode,
    useEffect,
    useRef,
} from 'react'
import {useLocation} from 'react-router-dom'
import {FadeSwitch} from 'swayok-react-mdb-ui-kit/components/Animation/FadeSwitch'
import {Loading} from 'swayok-react-mdb-ui-kit/components/Loading/Loading'
import {ScrollToTop} from 'swayok-react-mdb-ui-kit/components/ScrollToTop'
import {NavigationService} from 'swayok-react-mdb-ui-kit/services/NavigationService'

interface Props {
    children: ReactNode | ReactNode[]
    // false (по умолчанию) - пересоздает компонент страницы, если location.key меняется (soft page reload).
    // true - компонент страницы не пересоздается при смене location.key.
    notReloadable?: boolean
    //  Фиксированный ключ контейнера (отмена анимации при смене страницы)
    permanentKey?: string
    // CSS ID страницы.
    id?: string | null
    // Задержка показа индикатора загрузки.
    loadingIndicatorDelay?: number
}

// Приватные пути требуют авторизации.
export function PrivateRoute(props: Props) {

    const {
        children,
        notReloadable,
        permanentKey,
        id,
        loadingIndicatorDelay,
    } = props

    const loadingUserData = useAuthStore(
        state => state.loadingUserData
    )
    const user = useAuthStore(
        state => state.user
    )
    const setOnUserDataLoaded = useAuthStore(
        state => state.setOnUserDataLoaded
    )
    const location = useLocation()
    const setAppSection = useAppRootStore(
        state => state.setAppSection
    )
    const hideModals = useModalsStore(
        state => state.hideModals
    )

    const containerRef = useRef<HTMLDivElement>(null)

    // Настройка глобальных элементов интерфейса приложения для кабинета пользователя
    // пока загружаются данные пользователя.
    useConfigureCabinetUi(config => {
        if (!loadingUserData) {
            return null
        }
        config.topMenu.createAdButton = false
        config.topMenu.authBlock = false
        return config
    })

    // Настройка страницы при открытии.
    useEffect(() => {
        hideModals()
        setAppSection('private')

        // Ожидание загрузки данных пользователя.
        setOnUserDataLoaded((user: UserData | null) => {
            if (!user) {
                // Пользователь не авторизован - перенаправляем на авторизацию.
                NavigationService.navigate(appUrl().login, null, null, false, location)
                return
            }
            if (!user.registeredAt) {
                // Регистрация не завершена - перенаправляем на регистрацию.
                NavigationService.replace(appUrl().registration, null, null)
                return
            }
        })
    }, [])

    const loading: boolean = loadingUserData || !user

    return (
        <main
            className="private-page-content"
            id={id ?? undefined}
            key={permanentKey ?? (notReloadable ? '1' : location.key || '1')}
        >
            <MetaTags
                title={trans().cabinet.default_page_title}
                wrapTitle
            />
            <ScrollToTop key={location.pathname} />
            <FadeSwitch
                transitionKey={loading}
                transitionRef={containerRef}
            >
                {loading ? (
                    <div
                        ref={containerRef}
                        className="private-page-data-loading full-width full-height flex-center"
                    >
                        <Loading
                            loading={true}
                            showDelay={loadingIndicatorDelay}
                        />
                    </div>
                ) : (
                    <div ref={containerRef}>
                        {children}
                    </div>
                )}
            </FadeSwitch>
        </main>
    )
}
